import { zodResolver } from '@hookform/resolvers/zod';
import { useAuthStore } from '@smarthome/store';
import { ROUTES } from '@smarthome/ui';
import { Home } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { Alert, Button, EmailField, PasswordField } from '@/components/ui';

const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

/**
 * Login Page
 *
 * Web uses HTTP-only cookies for authentication.
 * The backend sets the cookie on successful login.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const { email, setEmail, setAuthenticated } = useAuthStore();
  const [serverError, setServerError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: email ?? '',
      password: '',
    },
  });

  async function onSubmit(data: LoginFormData) {
    setServerError(null);
    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Important for cookies
        body: JSON.stringify({ email: data.email, password: data.password }),
      });

      if (!response.ok) {
        const responseData = await response.json().catch(() => ({}));
        throw new Error(responseData.error ?? 'Login failed');
      }

      // Save email for next login
      setEmail(data.email);
      setAuthenticated(true);
      navigate(ROUTES.HOME);
    } catch (err) {
      setServerError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen">
      {/* Left side - Branding panel (hidden on mobile) */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 relative overflow-hidden">
        {/* Abstract decorative shapes */}
        <div className="absolute inset-0">
          <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute top-1/2 -right-32 h-[500px] w-[500px] rounded-full bg-blue-400/20 blur-3xl" />
          <div className="absolute -bottom-32 left-1/4 h-80 w-80 rounded-full bg-indigo-400/20 blur-3xl" />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm">
              <Home className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-semibold text-white">Smarthome</span>
          </div>

          <div className="max-w-md">
            <h1 className="text-4xl font-bold leading-tight text-white xl:text-5xl">
              Your home,
              <br />
              intelligently connected.
            </h1>
            <p className="mt-6 text-lg text-blue-100">
              Control your lights, climate, and devices from anywhere. Simple, secure, seamless.
            </p>
          </div>

          <p className="text-sm text-blue-200">Secure access to your smart home ecosystem</p>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="flex w-full items-center justify-center bg-white px-4 sm:px-6 dark:bg-gray-950 lg:w-1/2 xl:w-2/5">
        <div className="w-full max-w-sm">
          {/* Mobile logo - only shown on small screens */}
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600">
              <Home className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-semibold text-gray-900 dark:text-white">Smarthome</span>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Welcome back</h2>
            <p className="mt-2 text-gray-500 dark:text-gray-400">
              Enter your credentials to continue
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {serverError && <Alert variant="error">{serverError}</Alert>}

            <EmailField
              label="Email"
              {...register('email')}
              autoComplete="email"
              error={errors.email?.message}
            />

            <PasswordField
              label="Password"
              {...register('password')}
              autoComplete="current-password"
              error={errors.password?.message}
            />

            <Button type="submit" loading={isLoading} className="w-full">
              {isLoading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
